import { PLAYABLE_AREA, CEILING_HEIGHT, CANVAS_HEIGHT, FLOOR_HEIGHT } from '../constants';
import { GameMode } from '../../shared/sharedTypes';
import defaultSettings from '../defaultSettings';
import { createArcPlatformParams } from './arc';
import { createBinaryClustersParams } from './binaryCluster';
import { createRectangularPlatformParams } from './rectangle';
import { createSplineParams } from './spline';
import { platformDistributor } from './platformDistribution';
import { PATTERNS } from './patterns';
import { SeededRandom } from '../utils/SeededRandom';
import { BiomeSystem } from './biomes';

export function createPlatformParams(newRoom = true, socketClient: any, platformData: any[], seed?: number, customHeight?: number) {
    // Set building flag BEFORE checking to prevent race conditions
    const pm = (window as any).game?.platformManager;
    if (pm) {
        if (pm.isBuilding) {
            console.warn('Level generation already in progress, skipping duplicate createPlatformParams call');
            return;
        }
        // Set flag immediately to prevent concurrent calls
        pm.isBuilding = true;
    }

    const _settings = localStorage.getItem('golfUpSettings');
    const settings = _settings ? JSON.parse(_settings) : defaultSettings;

    // Use provided seed or generate from levelId, or fallback to random
    const levelSeed = seed !== undefined ? seed :
        (settings.roomSettings?.levelId ? parseInt(settings.roomSettings.levelId) :
            Math.floor(Math.random() * 1000000));

    // Initialize seeded random generator
    const rng = new SeededRandom(levelSeed);
    platformDistributor.reset(levelSeed);

    // Clear platformData array to ensure we start fresh
    platformData.length = 0;

    // --- Biome System: Create cohesive themed zones ---
    const START_Y = FLOOR_HEIGHT + 150;
    // Use custom height if provided (for campaign mode), otherwise use default CANVAS_HEIGHT
    const levelHeight = customHeight !== undefined ? customHeight : CANVAS_HEIGHT;
    const END_Y = levelHeight - CEILING_HEIGHT - 100;
    const totalHeight = END_Y - START_Y;

    // Initialize biome system for cohesive visual progression
    const biomeSystem = new BiomeSystem(levelSeed, totalHeight);

    // Store biome info for renderer (will be used by platformManager/renderManager)
    (platformData as any).biomeSystem = biomeSystem;
    (platformData as any).biomeMetadata = {
        totalHeight,
        startY: START_Y
    };

    // --- Smart Level Generator 2.0 (Pattern Based + Biome Themed) ---

    const BASE_MIN_JUMP_HEIGHT = 120;
    const BASE_MAX_JUMP_HEIGHT = 220;

    let currentY = START_Y;
    let currentX = PLAYABLE_AREA / 2;

    // Initial platform at the start - get biome and apply style
    const startBiome = biomeSystem.getBiomeAtHeight(currentY);
    const startStyle = biomeSystem.getPlatformStyle(startBiome);
    platformData.push({
        type: 'rectangularMesh',
        params: {
            centerX: PLAYABLE_AREA / 2,
            y: currentY,
            width: 400,
            height: 50,
            biomeName: startBiome.name,
            color: startStyle.color,
            highlightColor: startStyle.highlightColor,
            shadowColor: startStyle.shadowColor,
            roughness: startStyle.roughness,
            chamferRadius: startStyle.chamferRadius
        }
    });

    while (currentY < END_Y) {
        // Get biome for current height
        const currentBiome = biomeSystem.getBiomeAtHeight(currentY);
        const biomeStyle = biomeSystem.getPlatformStyle(currentBiome);
        const jumpHeights = biomeSystem.getAdjustedJumpHeight(BASE_MIN_JUMP_HEIGHT, BASE_MAX_JUMP_HEIGHT, currentBiome);

        const roll = rng.next();

        // 1. Try to apply a pattern (30% chance, but less likely in harder biomes)
        const patternChance = 0.3 * (1 / currentBiome.difficultyMultiplier); // Harder biomes = fewer patterns
        if (roll < patternChance && currentY < END_Y - 400) {
            const patternKeys = Object.keys(PATTERNS);
            const patternName = rng.choice(patternKeys);
            const pattern = PATTERNS[patternName as keyof typeof PATTERNS];

            // Apply pattern steps
            for (const step of pattern.steps) {
                currentY += step.dy;

                if (currentY > END_Y) break;

                if ('branch' in step && step.branch) {
                    // Create a secondary platform with biome styling
                    let branchX = currentX + step.dx;
                    branchX = Math.max(100, Math.min(PLAYABLE_AREA - 100, branchX));
                    const params = createRectangularPlatformParams(currentY, false);
                    params.centerX = branchX;
                    params.width = 80;
                    (params as any).biomeName = currentBiome.name;
                    (params as any).color = biomeStyle.color;
                    (params as any).highlightColor = biomeStyle.highlightColor;
                    (params as any).shadowColor = biomeStyle.shadowColor;
                    (params as any).roughness = biomeStyle.roughness;
                    (params as any).chamferRadius = biomeStyle.chamferRadius;
                    platformData.push({ type: 'rectangularMesh', params });
                } else if ('type' in step && step.type === "walls") {
                    // Tunnel: Two vertical walls with biome styling
                    const leftWall = createRectangularPlatformParams(currentY, false);
                    leftWall.centerX = currentX - 80;
                    leftWall.width = 20;
                    leftWall.height = 200;
                    (leftWall as any).biomeName = currentBiome.name;
                    (leftWall as any).color = biomeStyle.color;
                    (leftWall as any).roughness = biomeStyle.roughness;

                    const rightWall = createRectangularPlatformParams(currentY, false);
                    rightWall.centerX = currentX + 80;
                    rightWall.width = 20;
                    rightWall.height = 200;
                    (rightWall as any).biomeName = currentBiome.name;
                    (rightWall as any).color = biomeStyle.color;
                    (rightWall as any).roughness = biomeStyle.roughness;

                    platformData.push({ type: 'rectangularMesh', params: leftWall });
                    platformData.push({ type: 'rectangularMesh', params: rightWall });

                    // Add small platforms inside the tunnel
                    const stepP = createRectangularPlatformParams(currentY, false);
                    stepP.centerX = currentX;
                    stepP.width = 60;
                    (stepP as any).biomeName = currentBiome.name;
                    (stepP as any).color = biomeStyle.color;
                    (stepP as any).roughness = biomeStyle.roughness;
                    platformData.push({ type: 'rectangularMesh', params: stepP });

                } else {
                    // Standard step with biome styling
                    currentX += step.dx;
                    // Clamp X
                    if (currentX < 100) { currentX = 100; }
                    if (currentX > PLAYABLE_AREA - 100) { currentX = PLAYABLE_AREA - 100; }

                    const params = createRectangularPlatformParams(currentY, false);
                    params.centerX = currentX;
                    // Apply custom width if specified in pattern step
                    if ('width' in step && step.width) {
                        params.width = step.width;
                    }
                    (params as any).biomeName = currentBiome.name;
                    (params as any).color = biomeStyle.color;
                    (params as any).highlightColor = biomeStyle.highlightColor;
                    (params as any).shadowColor = biomeStyle.shadowColor;
                    (params as any).roughness = biomeStyle.roughness;
                    (params as any).chamferRadius = biomeStyle.chamferRadius;
                    platformData.push({ type: 'rectangularMesh', params });
                }
            }

        } else {
            // 2. Fallback to standard jump (70% chance) - use biome-adjusted difficulty
            const jumpHeight = rng.nextFloat(jumpHeights.min, jumpHeights.max);
            currentY += jumpHeight;

            if (currentY > END_Y) break;

            // Move X randomly but keep reachable (wider gaps in harder biomes)
            const maxOffset = 250 * currentBiome.difficultyMultiplier;
            let offset = (rng.next() * 2 - 1) * maxOffset;
            if (currentX < 150) offset += 100;
            if (currentX > PLAYABLE_AREA - 150) offset -= 100;

            currentX += offset;
            currentX = Math.max(100, Math.min(PLAYABLE_AREA - 100, currentX));

            // Choose platform type based on biome preferences
            const platformType = biomeSystem.choosePlatformType(currentBiome);
            let params: any;

            if (platformType === 'arc' || (rng.next() < 0.3 && currentBiome.platformTypes.includes('arc'))) {
                params = createArcPlatformParams(currentY, rng.next() > 0.5, false);
                params.centerX = currentX;
                (params as any).biomeName = currentBiome.name;
                (params as any).color = biomeStyle.color;
                (params as any).roughness = biomeStyle.roughness;
                platformData.push({ type: 'arcMesh', params });
            } else if (platformType === 'spline' || (rng.next() < 0.3 && currentBiome.platformTypes.includes('spline'))) {
                params = createSplineParams(currentY, false);
                (params as any).centerX = currentX;
                (params as any).biomeName = currentBiome.name;
                (params as any).color = biomeStyle.color;
                (params as any).roughness = biomeStyle.roughness;
                platformData.push({ type: 'splineMesh', params });
            } else {
                // Default to rectangular (or branch/ledge variants if biome supports them)
                params = createRectangularPlatformParams(currentY, false);
                params.centerX = currentX;
                (params as any).biomeName = currentBiome.name;
                (params as any).color = biomeStyle.color;
                (params as any).roughness = biomeStyle.roughness;
                platformData.push({ type: 'rectangularMesh', params });
            }
        }
    }

    // Ensure there's a final platform near the top to reach the goal
    const finalPlatformY = END_Y;
    const finalBiome = biomeSystem.getBiomeAtHeight(finalPlatformY);
    const finalStyle = biomeSystem.getPlatformStyle(finalBiome);
    platformData.push({
        type: 'rectangularMesh',
        params: {
            centerX: PLAYABLE_AREA / 2,
            y: finalPlatformY,
            width: 200,
            height: 40,
            biomeName: finalBiome.name,
            color: finalStyle.color,
            highlightColor: finalStyle.highlightColor,
            shadowColor: finalStyle.shadowColor,
            roughness: finalStyle.roughness,
            chamferRadius: finalStyle.chamferRadius
        }
    });


    const noGoalModes = [GameMode.CoinDash, GameMode.EscapeBall];
    if (!noGoalModes.includes(settings.roomSettings.gameMode)) {
        makeGoal.call(this);
    }

    function makeGoal() {
        if (settings.roomSettings.goalType === 'finish-line') {
            const finishLineParams = {
                centerX: PLAYABLE_AREA / 2,
                y: (CEILING_HEIGHT / 2),
                width: PLAYABLE_AREA,
                height: CEILING_HEIGHT,
                isGoal: true,
                goalType: 'finish-line'
            };
            platformData.push({ type: 'rectangularMesh', params: finishLineParams });
        } else if (settings.roomSettings.goalType === 'bucket') {

            // Create bucket arc
            const bucketArcParams = {
                centerX: PLAYABLE_AREA / 2,
                y: 250,
                radius: 80,
                thickness: 40,
                startAngle: -0.2,
                endAngle: Math.PI + 0.2,
                divisions: 8,
                isGoal: true,
                goalType: 'bucket'
            };
            platformData.push({ type: 'arcMesh', params: bucketArcParams });
        }
    }

    // Use the seed as levelId for consistency
    if (!settings.roomSettings.levelId) {
        settings.roomSettings.levelId = levelSeed.toString();
    }

    console.log("platform params have been created", platformData);
    socketClient.platformsCreatedLocally(settings, platformData.sort((a: any, b: any) => b.height - a.height), newRoom);
}